import './CreateDialogSortingMachine.scss';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

import useAxiosHook from '../../../../../hooks/useAxiosHook';
import useMediaQuery from '../../../../../hooks/useMediaQuery';
import useToastMessage from '../../../../../hooks/useToastMessage';
import { sequential } from '../../../../../utils/helpers/functions';
import { SortingMachinesType } from '../../SortingMachine.functions';
import { CreateSortingMachinesType } from './CreateDialogSortingMachine.functions';
import FormContainer from './FormContainer';

type Props = {
  isShown: boolean;
  data: SortingMachinesType | undefined;
  isEditDialog: boolean;
  onHide: () => void;
  reloadCollection: () => void;
};

function CreateDialog({
  isShown,
  data,
  isEditDialog,
  onHide,
  reloadCollection,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const isOnMobile = useMediaQuery('(max-width: 768px)');

  const {
    data: createData,
    error: createError,
    isLoading: isCreateLoading,
    reload: createReload,
  } = useAxiosHook(
    {
      url: '/orders/sorting/machine',
      method: 'POST',
    },
    {
      skipWhen: true,
    }
  );
  const {
    data: editData,
    error: editError,
    isLoading: isEditLoading,
    reload: editReload,
  } = useAxiosHook(
    {
      url: `/orders/sorting/machine/${data?.id}`,
      method: 'PUT',
    },
    {
      skipWhen: true,
    }
  );

  useToastMessage(createData, createError, {
    success: {
      summary: t('The sorting machine has been created successfully.'),
      callback: () => sequential(onHide, reloadCollection),
    },
    error: {
      summary: t('An error occurred while creating the sorting machine.'),
      callback: () => {
        console.error('createError ', createError);
        if (createError?.response?.status === 409) {
          return t('The sorting machine already exists.');
        }
        return t('An error occurred while creating the sorting machine.');
      },
    },
  });

  useToastMessage(editData, editError, {
    success: {
      summary: t('The sorting machine has been updated successfully.'),
      callback: () => sequential(onHide, reloadCollection),
    },
    error: {
      summary: t('An error occurred while updating the sorting machine.'),
      callback: () => {
        console.error('editError ', editError);
        if (editError?.response?.status === 409) {
          return t('The sorting machine already exists.');
        }
        return t('An error occurred while updating the sorting machine.');
      },
    },
  });

  const dialogFooter = (
    <>
      <Button
        type="button"
        label={t('Cancel')}
        className="p-button-text"
        onClick={() => onHide()}
      />
      <Button
        type="submit"
        form="sorting-machine-create-edit-form"
        label={
          isEditLoading || isCreateLoading
            ? t('Saving...')
            : isEditDialog
            ? t('Save changes')
            : t('Add sorting machine')
        }
        disabled={isEditLoading || isCreateLoading}
      />
    </>
  );

  function handleCreation(data: CreateSortingMachinesType) {
    createReload({ data });
  }

  function handleEditing(data: CreateSortingMachinesType) {
    editReload({ data });
  }

  return (
    <Dialog
      header={
        isEditDialog ? t('Edit sorting machine') : t('Create sorting machine')
      }
      footer={dialogFooter}
      visible={isShown}
      resizable={false}
      onHide={onHide}
      maximizable
      maximized={isOnMobile}
      style={{ width: 680, maxWidth: '100%' }}
      className="create-sorting-machines-page-dialog"
    >
      {/* {isEditLoading || isCreateLoading ? (
        <DialogSpinner />
      ) : ( */}
      <FormContainer
        isEditDialog={isEditDialog}
        sortingMachineData={data}
        handleCreation={handleCreation}
        handleEditing={handleEditing}
      />
      {/* )} */}
    </Dialog>
  );
}

export default CreateDialog;
